import { getWeek, isSameWeek, isThisWeek, isWeekend } from 'date-fns';

import { DeliveryTime } from 'api/api';

import {
  addDaysToNow,
  formatTime,
  getDeliveryWeeks,
  respondWithEndTime,
  respondWithStartTime,
} from '../commons/utils/delivery-time';
import { availableLocaleConfigs } from '../commons/utils/translations/localization';
import { FormatDateFn, TranslateICUFn } from '../commons/utils/translations/translate';

export const formatMainDeliveryTimePart = ({
  deliveryTime,
  translate,
  dateFormatter,
  locale,
  isTomorrowHoliday,
}: {
  deliveryTime: DeliveryTime;
  translate: TranslateICUFn;
  dateFormatter: FormatDateFn;
  locale: string;
  isTomorrowHoliday: boolean;
}) => {
  const { max, unit, format = 'days', custom_text } = deliveryTime;
  const deliveryDate = addDaysToNow(deliveryTime.max);
  const localeConfig = availableLocaleConfigs[locale];
  if (custom_text) {
    return custom_text;
  }

  if ((format === 'weeks' || format === 'weeks_numbers') && unit === 'day') {
    const deliveryDateIsNextWeek = isSameWeek(deliveryDate, addDaysToNow(7), {
      locale: localeConfig,
    });

    if (isThisWeek(deliveryDate, { locale: localeConfig })) {
      return capitalize(translate('widget.delivery_time.THIS_WEEK'));
    }
    if (deliveryDateIsNextWeek) {
      return capitalize(translate('widget.delivery_time.NEXT_WEEK'));
    }
  }

  if (unit === 'business_day' || unit === 'day') {
    if (max === 0) {
      return capitalize(translate('widget.delivery_time.TODAY'));
    }
    if (max === 1 && deliveryTime.unit === 'day') {
      return capitalize(
        isTomorrowHoliday
          ? translate('widget.delivery_time.MAX_BUSINESS_DAYS', { max: 1 })
          : translate('widget.delivery_time.TOMORROW')
      );
    }
    if (max === 1 && deliveryTime.unit === 'business_day') {
      return isWeekend(addDaysToNow(max)) || isTomorrowHoliday
        ? translate('widget.delivery_time.modal.BUSINESS_DAYS', { day: max })
        : capitalize(translate('widget.delivery_time.TOMORROW'));
    }
  }

  switch (deliveryTime.unit) {
    case 'business_day':
    case 'day':
      switch (format) {
        case 'days':
          return translate(
            deliveryTime.unit === 'business_day'
              ? 'widget.delivery_time.modal.BUSINESS_DAYS'
              : 'widget.delivery_time.modal.DAYS',
            {
              day: deliveryTime.max,
            }
          );

        case 'date':
          return capitalize(dateFormatter(deliveryDate, 'do MMMM'));

        case 'weekday':
        case 'weekday_date':
          return capitalize(dateFormatter(deliveryDate, 'EEEE'));

        case 'weeks':
          const { deliveryWeekMax } = getDeliveryWeeks(deliveryTime, locale);
          return capitalize(
            translate('widget.delivery_time.MAX_WEEKS_EXACT', {
              max: deliveryWeekMax,
            })
          );

        case 'weeks_numbers': {
          return capitalize(
            translate('widget.delivery_time.modal.MAX_WEEK_NUMBER', {
              max: getWeek(deliveryDate, { locale: localeConfig }),
            })
          );
        }
      }
    case 'hour':
      return translate('widget.delivery_time.modal.HOURS', { hour: max });
    case 'minute':
      return translate('widget.delivery_time.modal.MINUTES', { minute: max });
  }
};

export const formatSecondaryDeliveryTimePart = (max: number, formatFn: FormatDateFn) =>
  formatFn(addDaysToNow(max), 'do MMMM');

export const getTimeSpanFromDeliveryTime = (deliveryTime: DeliveryTime, translate: any) => {
  const { use_timespan, timespan } = deliveryTime;
  if (use_timespan && timespan) {
    if (timespan.from && timespan.to) {
      return `${formatTime(timespan.from, translate)} \u2013 ${formatTime(timespan.to, translate)}`;
    } else if (timespan.from) {
      return respondWithStartTime(timespan.from, translate);
    } else if (timespan.to) {
      return respondWithEndTime(timespan.to, translate);
    }
  }
  return null;
};

const capitalize = (entry: string) => {
  return entry.charAt(0).toUpperCase() + entry.slice(1);
};
